<template>
  <div>
    <Navbar activeDirectory="admin" activePage="admin-assets"></Navbar>
    <div class="container-fluid py-4">

      <!-- ===== PAGE HEADER ===== -->
      <div class="d-flex align-items-center justify-content-between flex-wrap mb-4" style="gap:12px;">
        <div class="d-flex align-items-center">
          <div class="au-header-icon mr-3">
            <i class="mdi mdi-cogs"></i>
          </div>
          <div class="text-left">
            <h3 class="mb-0 font-weight-bold text-dark">
              Catalogo Tipologie Asset
            </h3>
            <p class="mb-0 text-muted small">
              Gestisci le tipologie di asset e i loro campi dinamici (catalogo master)
            </p>
          </div>
        </div>
        <button
          v-if="mode === 'read'"
          class="au-btn-submit"
          @click="setAddMode"
        >
          <i class="mdi mdi-plus mr-1"></i> Aggiungi Nuovo
        </button>
        <button v-else-if="mode === 'add' || mode === 'edit'" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'read'">
          <i class="mdi mdi-arrow-left mr-1"></i> Torna all'elenco
        </button>
      </div>

      <!-- === MODALITÀ SVILUPPATORE (D): catalogo tipologie asset === -->
      <div v-if="isDeveloper">

        <!-- DETAIL DRAWER OVERLAY -->
        <transition name="drawer-fade">
          <div v-if="mode === 'detail'" class="detail-overlay" @click.self="mode = 'read'">
            <div class="detail-drawer">
              <!-- Drawer Header -->
              <div class="detail-drawer-header">
                <div class="d-flex align-items-center">
                  <div class="detail-type-icon mr-3">
                    <i class="mdi mdi-cog-outline"></i>
                  </div>
                  <div class="text-left">
                    <p class="text-muted small mb-0" style="font-size:0.72rem;font-weight:700;text-transform:uppercase;letter-spacing:0.07em;">Tipologia Asset</p>
                    <h5 class="mb-0 font-weight-bold text-dark">{{ selectedAssetType.nome }}</h5>
                  </div>
                </div>
                <button class="detail-close-btn" @click="mode = 'read'">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>

              <!-- Drawer Body -->
              <div class="detail-drawer-body">

                <!-- Info generali -->
                <div class="detail-section mb-4">
                  <div class="detail-section-title">Informazioni Generali</div>
                  <div class="detail-info-grid">
                    <div class="detail-info-item">
                      <span class="detail-info-label">ID</span>
                      <span class="detail-info-value">#{{ selectedAssetType.id }}</span>
                    </div>
                    <div class="detail-info-item">
                      <span class="detail-info-label">Codice</span>
                      <code class="detail-info-code">{{ selectedAssetType.codice }}</code>
                    </div>
                    <div class="detail-info-item" style="grid-column: 1 / -1;">
                      <span class="detail-info-label">Modulo</span>
                      <span class="au-company-badge text-primary" style="background:#FEF1E0;">
                        {{ selectedAssetType.module_name || '—' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Schema Campi -->
                <div class="detail-section">
                  <div class="detail-section-title">
                    Campi Configurati
                    <span class="detail-fields-count">{{ (selectedAssetType.campi_schema || []).length }}</span>
                  </div>

                  <div v-if="!selectedAssetType.campi_schema || selectedAssetType.campi_schema.length === 0"
                       class="detail-empty-fields">
                    <i class="mdi mdi-text-box-outline mb-2" style="font-size:2rem;color:#cbd5e1;"></i>
                    <p class="text-muted small mb-0">Nessun campo definito per questa tipologia.</p>
                  </div>

                  <div v-else class="detail-fields-list">
                    <div
                      v-for="(field, idx) in selectedAssetType.campi_schema"
                      :key="idx"
                      class="detail-field-card"
                    >
                      <div class="detail-field-index">{{ idx + 1 }}</div>
                      <div class="detail-field-info">
                        <div class="detail-field-name">{{ field.label || field.name }}</div>
                        <div class="detail-field-meta">
                          <span class="detail-field-badge detail-badge-name">
                            <i class="mdi mdi-code-tags mr-1"></i>{{ field.name }}
                          </span>
                          <span class="detail-field-badge detail-badge-type">
                            <i class="mdi mdi-format-list-checks mr-1"></i>{{ field.type || 'text' }}
                          </span>
                          <span v-if="field.required" class="detail-field-badge detail-badge-required">
                            <i class="mdi mdi-asterisk mr-1"></i>Obbligatorio
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Interventi Associati -->
                <div class="detail-section mb-4">
                  <div class="detail-section-title">
                    Interventi Associati
                    <span class="detail-fields-count">{{ (selectedAssetType.interventi_ids || []).length }}</span>
                  </div>
                  <div v-if="!selectedAssetType.interventi_ids || selectedAssetType.interventi_ids.length === 0" class="detail-empty-fields mt-3">
                    <p class="text-muted small mb-0">Nessun intervento associato.</p>
                  </div>
                  <div v-else class="cw-chips mt-3">
                    <span v-for="intId in selectedAssetType.interventi_ids" :key="'chip-'+intId" class="cw-chip cw-chip-active">
                      {{ getInterventionName(intId) }}
                    </span>
                  </div>
                </div>

              </div>

              <!-- Drawer Footer -->
              <div class="detail-drawer-footer">
                <button class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'read'">
                  <i class="mdi mdi-arrow-left mr-1"></i> Chiudi
                </button>
                <div class="d-flex" style="gap:8px;">
                  <button
                    class="btn btn-outline-primary rounded-pill px-4 font-weight-semibold"
                    @click="editAssetType(selectedAssetType)"
                  >
                    <i class="mdi mdi-pencil-outline mr-1"></i> Modifica
                  </button>
                  <button
                    class="btn btn-outline-danger rounded-pill px-4 font-weight-semibold"
                    @click="deleteAssetType(selectedAssetType); mode = 'read'"
                  >
                    <i class="mdi mdi-trash-can-outline mr-1"></i> Elimina
                  </button>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <!-- LIST MODE (D) -->
        <div v-if="mode === 'read' || mode === 'detail'">
          <div class="au-card">
            <div class="cw-section-title">Catalogo Tipologie Asset</div>
            <div class="table-responsive">
              <table class="au-table">
                <thead>
                  <tr>
                    <th style="width: 80px;">ID</th>
                    <th>Nome Tipologia</th>
                    <th>Codice</th>
                    <th>Modulo</th>
                    <th>Campi</th>
                    <th class="text-right" style="width: 160px;">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="6" class="text-center py-4">
                      <span class="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                      Caricamento tipologie...
                    </td>
                  </tr>
                  <tr v-else-if="assetTypes.length === 0">
                    <td colspan="6" class="text-center py-4 text-muted">
                      Nessuna tipologia trovata.
                    </td>
                  </tr>
                  <tr
                    v-for="item in assetTypes"
                    :key="item.id"
                    v-else
                    class="asset-row-clickable"
                    @click="viewAssetType(item)"
                  >
                    <td class="font-weight-bold text-muted">{{ item.id }}</td>
                    <td><span class="font-weight-bold text-dark">{{ item.nome }}</span></td>
                    <td><code class="text-primary">{{ item.codice }}</code></td>
                    <td>
                      <span v-if="item.module_name" class="au-company-badge text-primary" style="background:#FEF1E0;">
                        {{ item.module_name }}
                      </span>
                    </td>
                    <td>
                      <span class="detail-fields-count" style="cursor:default;">
                        {{ (item.campi_schema || []).length }}
                      </span>
                    </td>
                    <td class="text-right text-nowrap" @click.stop>
                      <button
                        class="btn btn-sm btn-outline-primary rounded-pill px-3 font-weight-semibold mr-1"
                        @click="viewAssetType(item)"
                      >
                        <i class="mdi mdi-eye-outline mr-1"></i>Dettagli
                      </button>
                      <button
                        class="btn btn-sm btn-outline-danger rounded-pill px-3 font-weight-semibold"
                        @click="deleteAssetType(item)"
                      >
                        Elimina
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- ADD FORM MODE (D) -->
        <div class="row justify-content-center" v-if="mode === 'add'">
          <div class="col-lg-8 col-md-10 mb-4">
            <div class="au-card h-100">
              <div class="cw-section-title">
                <span class="cw-step-num">1</span>Nuova Tipologia Asset
              </div>
              <form @submit.prevent="saveAssetType">
                <div class="form-group mb-4 text-left">
                  <label class="cw-label">Modulo <span class="text-danger">*</span></label>
                  <div class="cw-select-wrap">
                    <i class="mdi mdi-layers-outline cw-field-icon"></i>
                    <select v-model="formType.fk_module" class="cw-select" required>
                      <option value="" disabled>Seleziona modulo...</option>
                      <option v-for="mod in modules" :key="mod.id" :value="mod.id">{{ mod.nome }}</option>
                    </select>
                    <i class="mdi mdi-chevron-down cw-select-arrow"></i>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6 form-group mb-4 text-left">
                    <label class="cw-label">Nome Tipologia <span class="text-danger">*</span></label>
                    <div class="cw-input-wrap">
                      <i class="mdi mdi-tag-text-outline cw-field-icon"></i>
                      <input v-model="formType.nome" class="cw-input" placeholder="Es. Lampione" required @input="generateCodice">
                    </div>
                  </div>
                  <div class="col-md-6 form-group mb-4 text-left">
                    <label class="cw-label">Codice (Autogenerato) <span class="text-danger">*</span></label>
                    <div class="cw-input-wrap">
                      <i class="mdi mdi-code-tags cw-field-icon"></i>
                      <input v-model="formType.codice" class="cw-input" style="background:#f8fafc;" required readonly>
                    </div>
                  </div>
                </div>
                
                <div class="cw-section-title mt-4">
                  <span class="cw-step-num">2</span>Configurazione Campi (JSON Schema)
                </div>
                <div class="form-group mb-4 text-left">
                  <label class="cw-label">Definisci i campi richiesti per questo asset (formato JSON Array)</label>
                  <textarea v-model="formType.campi_schema_str" class="cw-input" style="height: 150px; padding-top: 14px;" placeholder='[{"name": "potenza", "type": "number", "label": "Potenza (W)"}, {"name": "stato", "type": "text", "label": "Stato di usura"}]'></textarea>
                </div>
                
                <div class="cw-footer mt-4 d-flex align-items-center justify-content-between flex-wrap">
                  <small class="text-muted">
                    <i class="mdi mdi-asterisk text-danger" style="font-size:0.6rem;"></i> Campi obbligatori
                  </small>
                  <div class="d-flex" style="gap:12px;">
                    <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'read'">Annulla</button>
                    <button type="submit" class="cw-btn-submit" :disabled="submitting">
                      <span class="spinner-border spinner-border-sm mr-2" v-if="submitting"></span>
                      <i class="mdi mdi-check-circle-outline mr-1" v-else></i>
                      Salva Tipologia
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <!-- EDIT FORM MODE (D) -->
        <div class="row justify-content-center" v-if="mode === 'edit'">
          <div class="col-lg-8 col-md-10 mb-4">
            <div class="au-card h-100">
              <!-- Edit banner -->
              <div class="edit-mode-banner mb-4">
                <i class="mdi mdi-pencil-circle-outline mr-2" style="font-size:1.2rem;"></i>
                Stai modificando la tipologia <strong>{{ formEdit.nome }}</strong>
              </div>

              <div class="cw-section-title">
                <span class="cw-step-num">1</span>Informazioni Tipologia
              </div>
              <form @submit.prevent="updateAssetType">
                <div class="row">
                  <div class="col-md-6 form-group mb-4 text-left">
                    <label class="cw-label">Nome Tipologia <span class="text-danger">*</span></label>
                    <div class="cw-input-wrap">
                      <i class="mdi mdi-tag-text-outline cw-field-icon"></i>
                      <input v-model="formEdit.nome" class="cw-input" placeholder="Es. Lampione" required @input="generateEditCodice">
                    </div>
                  </div>
                  <div class="col-md-6 form-group mb-4 text-left">
                    <label class="cw-label">Codice <span class="text-danger">*</span></label>
                    <div class="cw-input-wrap">
                      <i class="mdi mdi-code-tags cw-field-icon"></i>
                      <input v-model="formEdit.codice" class="cw-input" required>
                    </div>
                  </div>
                </div>

                <div class="form-group mb-4 text-left">
                  <label class="cw-label">Modulo</label>
                  <div class="cw-input-wrap">
                    <i class="mdi mdi-layers-outline cw-field-icon"></i>
                    <input :value="formEdit.module_name" class="cw-input" style="background:#f8fafc; color:#94a3b8;" disabled>
                  </div>
                  <small class="text-muted" style="font-size:0.72rem;">Il modulo non è modificabile dopo la creazione.</small>
                </div>

                <div class="cw-section-title mt-4">
                  <span class="cw-step-num">2</span>Configurazione Campi (JSON Schema)
                </div>
                <div class="form-group mb-4 text-left">
                  <label class="cw-label">Modifica i campi dell'asset (formato JSON Array)</label>
                  <textarea
                    v-model="formEdit.campi_schema_str"
                    class="cw-input"
                    style="height: 180px; padding-top: 14px; font-family: monospace; font-size: 0.82rem;"
                    placeholder='[{"name": "potenza", "type": "number", "label": "Potenza (W)"}]'
                  ></textarea>
                  <small class="text-muted d-block mt-1" style="font-size:0.72rem;">
                    <i class="mdi mdi-alert-outline text-warning mr-1"></i>
                    Attenzione: modificare i campi può influenzare gli asset già inseriti dalle aziende.
                  </small>
                </div>

                <div class="cw-section-title mt-4">
                  <span class="cw-step-num">3</span>Interventi Associati
                </div>
                <div class="form-group mb-4 text-left">
                  <label class="cw-label">Seleziona le tipologie di intervento permesse per questo asset</label>
                  <div class="d-flex flex-wrap mt-2" style="gap: 8px;">
                    <label v-for="int in allInterventions.filter(i => i.fk_module === formEdit.fk_module)" :key="int.id" class="cw-chip" :class="{'cw-chip-active': formEdit.interventi_ids.includes(int.id)}" style="cursor:pointer; margin:0;">
                      <input type="checkbox" :value="int.id" v-model="formEdit.interventi_ids" style="display:none;">
                      {{ int.nome }}
                    </label>
                  </div>
                  <small class="text-muted d-block mt-2" v-if="allInterventions.filter(i => i.fk_module === formEdit.fk_module).length === 0">
                    Nessun intervento trovato per questo modulo.
                  </small>
                </div>

                <div class="cw-footer mt-4 d-flex align-items-center justify-content-between flex-wrap">
                  <small class="text-muted">
                    <i class="mdi mdi-asterisk text-danger" style="font-size:0.6rem;"></i> Campi obbligatori
                  </small>
                  <div class="d-flex" style="gap:12px;">
                    <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'read'">Annulla</button>
                    <button type="submit" class="cw-btn-submit" :disabled="submitting">
                      <span class="spinner-border spinner-border-sm mr-2" v-if="submitting"></span>
                      <i class="mdi mdi-content-save-outline mr-1" v-else></i>
                      Aggiorna Tipologia
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { Navbar } from "@/components";
import { parseCampiSchema } from "@/utils/assetSchema";

export default {
  components: { Navbar },
  data() {
    return {
      tipologia: localStorage.getItem("tipologia"),
      mode: "read",
      loading: false,
      submitting: false,
      
      modules: [],
      
      // Dev state
      assetTypes: [],
      selectedAssetType: null,
      formType: {
        fk_module: "",
        nome: "",
        codice: "",
        campi_schema_str: '[\n  {"name": "campo_1", "type": "text", "label": "Etichetta"}\n]'
      },
      formEdit: {
        id: null,
        nome: "",
        codice: "",
        fk_module: "",
        module_name: "",
        campi_schema_str: "",
        interventi_ids: []
      },
      allInterventions: []
    };
  },
  computed: {
    isDeveloper() {
      return this.tipologia === "D";
    }
  },
  mounted() {
    // La pagina serve ora esclusivamente il Catalogo Tipologie Asset del Developer.
    // Gli Admin_Azienda (B/A) vengono intercettati dal redirect di route (beforeEnter)
    // verso admin-modules?tab=assets; qui manteniamo coerenza servendo solo 'D'.
    if (this.tipologia !== "D") {
      this.$router.push({ name: "home" });
      return;
    }
    this.fetchModules();

    // Gestione Query Params (Developer)
    const action = this.$route.query.action;
    const qModule = this.$route.query.fk_module;

    this.fetchAssetTypes();
    this.fetchAllInterventions();
    if (action === 'add') {
      this.setAddMode();
      if (qModule) {
        this.formType.fk_module = parseInt(qModule);
      }
    }
  },
  methods: {
    fetchModules() {
      this.axios.get('/modules').then((response) => {
        this.modules = Array.isArray(response.data) ? response.data : [];
      }).catch(console.error);
    },
    
    // ==========================================
    // DEVELOPER METHODS (Tipologia 'D')
    // ==========================================
    fetchAllInterventions() {
      this.axios.get('/tipologie-intervento').then(res => {
        this.allInterventions = Array.isArray(res.data) ? res.data : [];
      }).catch(console.error);
    },
    getInterventionName(id) {
      const found = this.allInterventions.find(i => i.id === id);
      return found ? found.nome : `ID #${id}`;
    },
    viewAssetType(item) {
      this.selectedAssetType = item;
      this.mode = 'detail';
    },
    editAssetType(item) {
      this.formEdit.id = item.id;
      this.formEdit.nome = item.nome;
      this.formEdit.codice = item.codice;
      this.formEdit.fk_module = item.fk_module;
      this.formEdit.module_name = item.module_name || '';
      this.formEdit.interventi_ids = item.interventi_ids ? [...item.interventi_ids] : [];
      const schema = parseCampiSchema(item.campi_schema);
      this.formEdit.campi_schema_str = JSON.stringify(schema, null, 2);
      this.mode = 'edit';
    },
    generateEditCodice() {
      if (!this.formEdit.nome) { this.formEdit.codice = ''; return; }
      this.formEdit.codice = this.formEdit.nome
        .toLowerCase().trim()
        .replace(/[\s-]+/g, '_')
        .replace(/[^\w]/g, '');
    },
    updateAssetType() {
      let schemaParsed = [];
      try {
        schemaParsed = JSON.parse(this.formEdit.campi_schema_str);
      } catch(e) {
        this.$swal.fire('Errore JSON', 'Il formato dello schema campi non è un JSON valido.', 'error');
        return;
      }
      this.submitting = true;
      this.axios.put(`/asset_types/${this.formEdit.id}`, {
        nome: this.formEdit.nome,
        codice: this.formEdit.codice,
        campi_schema: schemaParsed
      }).then(res => {
        if (res.data && res.data.success) {
          return this.axios.post(`/asset_types/${this.formEdit.id}/interventi`, {
            interventi_ids: this.formEdit.interventi_ids
          });
        } else {
          throw new Error(res.data.error || 'Impossibile aggiornare tipologia');
        }
      }).then(res => {
        if (res && res.data && !res.data.success) {
           console.warn("Errore salvataggio interventi", res.data);
        }
        this.$swal.fire('Aggiornato', 'Tipologia modificata con successo', 'success');
        this.mode = 'read';
        this.fetchAssetTypes();
      }).catch(err => {
        this.$swal.fire('Errore', err.message || 'Impossibile aggiornare', 'error');
      }).finally(() => { this.submitting = false; });
    },
    fetchAssetTypes() {
      this.loading = true;
      this.axios.get('/asset_types')
        .then((res) => {
          this.assetTypes = (res.data && res.data.data) ? res.data.data : [];
        })
        .catch(console.error)
        .finally(() => { this.loading = false; });
    },
    setAddMode() {
      this.mode = "add";
      this.formType.fk_module = "";
      this.formType.nome = "";
      this.formType.codice = "";
    },
    generateCodice() {
      if (!this.formType.nome) { this.formType.codice = ""; return; }
      this.formType.codice = this.formType.nome
        .toLowerCase().trim()
        .replace(/[\s-]+/g, "_")
        .replace(/[^\w]/g, "");
    },
    saveAssetType() {
      let schemaParsed = [];
      try {
        schemaParsed = JSON.parse(this.formType.campi_schema_str);
      } catch(e) {
        this.$swal.fire("Errore JSON", "Il formato dello schema campi non è un JSON valido.", "error");
        return;
      }
      
      this.submitting = true;
      this.axios.post('/asset_types', {
        fk_module: this.formType.fk_module,
        nome: this.formType.nome,
        codice: this.formType.codice,
        campi_schema: schemaParsed
      }).then((res) => {
        if (res.data && res.data.success) {
          this.$swal.fire("Salvato", "Tipologia asset creata", "success");
          this.mode = "read";
          this.fetchAssetTypes();
        } else {
          this.$swal.fire("Errore", res.data.error || "Impossibile salvare", "error");
        }
      }).catch(console.error).finally(() => { this.submitting = false; });
    },
    deleteAssetType(item) {
      this.$swal.fire({
        title: "Elimina tipologia?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sì, elimina",
        cancelButtonText: "Annulla"
      }).then((result) => {
        if (result.value) {
          this.axios.delete(`/asset_types/${item.id}`)
            .then(res => {
              if (res.data && res.data.success) {
                this.$swal.fire("Eliminato!", "Tipologia rimossa", "success");
                this.fetchAssetTypes();
              } else {
                this.$swal.fire("Errore", res.data.error || "Impossibile eliminare", "error");
              }
            });
        }
      });
    }
  }
};
</script>

<style scoped>
/* =========================================
   DETAIL DRAWER — Asset Type
   ========================================= */

.detail-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(3px);
  z-index: 1050;
  display: flex;
  justify-content: flex-end;
}

.detail-drawer {
  width: 480px;
  max-width: 95vw;
  background: #fff;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 40px rgba(0, 0, 0, 0.15);
  animation: slideInRight 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideInRight {
  from { transform: translateX(100%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
}

.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.25s ease;
}
.drawer-fade-enter,
.drawer-fade-leave-to {
  opacity: 0;
}

.detail-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.detail-type-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.detail-type-icon .mdi {
  font-size: 1.4rem;
  color: #fff;
}

.detail-close-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #64748b;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.detail-close-btn:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}

.detail-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 28px;
}

.detail-drawer-footer {
  padding: 20px 28px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  background: #fafbfc;
}

.detail-section {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #f1f5f9;
}

.detail-section-title {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-fields-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 20px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 800;
}

.detail-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.detail-info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-info-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #94a3b8;
}

.detail-info-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
}

.detail-info-code {
  font-size: 0.88rem;
  color: #F7931E;
  background: #FEF1E0;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 700;
}

.detail-empty-fields {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
}

.detail-fields-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-field-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  transition: all 0.2s;
}
.detail-field-card:hover {
  border-color: #F5B871;
  box-shadow: 0 4px 12px rgba(247, 147, 30, 0.08);
  transform: translateX(2px);
}

.detail-field-index {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #FEF1E0;
  color: #E07B00;
  font-size: 0.72rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.detail-field-info {
  flex: 1;
}

.detail-field-name {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
}

.detail-field-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.detail-field-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.detail-badge-name {
  background: #f1f5f9;
  color: #475569;
}

.detail-badge-type {
  background: #FEF1E0;
  color: #7c3aed;
}

.detail-badge-required {
  background: #fef2f2;
  color: #dc2626;
}

/* Row clickable style */
.asset-row-clickable {
  cursor: pointer;
  transition: background 0.15s;
}
.asset-row-clickable:hover {
  background: #FEF1E0 !important;
}
/* =========================================
   ADMIN ASSETS — Premium UI (RoadME DS)
   ========================================= */

.cw-section-title {
  font-size: 0.82rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: #64748b; margin-bottom: 22px;
  display: flex; align-items: center;
}
.cw-step-num {
  width: 22px; height: 22px; border-radius: 50%;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.7rem; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
  margin-right: 10px; flex-shrink: 0;
}
.cw-label {
  font-size: 0.75rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em;
  color: #94a3b8; margin-bottom: 8px; display: block;
}
.cw-input-wrap { position: relative; display: flex; align-items: center; }
.cw-field-icon {
  position: absolute; left: 12px;
  color: #94a3b8; font-size: 1rem; z-index: 2; pointer-events: none;
}
.cw-input {
  width: 100%; height: 46px; padding: 0 14px 0 36px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; font-size: 0.92rem; font-weight: 500; color: #1e293b;
  transition: border-color 0.2s, box-shadow 0.2s; outline: none;
}
.cw-input:focus {
  border-color: #F7931E; box-shadow: 0 0 0 3px rgba(247,147,30,0.12); background: #fff;
}
.cw-select-wrap { position: relative; display: flex; align-items: center; }
.cw-select {
  width: 100%; height: 46px; padding: 0 38px 0 36px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; appearance: none; -webkit-appearance: none;
  font-size: 0.92rem; font-weight: 500; color: #1e293b;
  cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; outline: none;
}
.cw-select:focus {
  border-color: #F7931E; box-shadow: 0 0 0 3px rgba(247,147,30,0.12); background: #fff;
}
.cw-select-arrow {
  position: absolute; right: 14px;
  color: #94a3b8; font-size: 1rem; pointer-events: none;
}
.cw-btn-submit {
  display: inline-flex; align-items: center;
  padding: 10px 24px; border: none; border-radius: 30px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.88rem; font-weight: 700;
  cursor: pointer; box-shadow: 0 4px 14px rgba(247,147,30,0.35);
  transition: all 0.2s;
}
.cw-btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #E07B00, #C96A00);
  box-shadow: 0 6px 18px rgba(247,147,30,0.45); transform: translateY(-1px);
}
.cw-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
.cw-footer {
  background: #fff; border-radius: 14px;
  padding: 18px 24px; border: 1px solid rgba(0,0,0,0.05);
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

.au-header-icon {
  width: 48px; height: 48px; border-radius: 14px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(247,147,30,0.35); flex-shrink: 0;
}
.au-header-icon .mdi { color: #fff; font-size: 1.5rem; }

.au-card {
  background: #fff; border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  padding: 28px; border: 1px solid rgba(0,0,0,0.05);
}

.au-btn-submit {
  display: inline-flex; align-items: center;
  padding: 10px 24px; border: none; border-radius: 30px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.88rem; font-weight: 700;
  cursor: pointer; box-shadow: 0 4px 14px rgba(247,147,30,0.35);
  transition: all 0.2s;
}
.au-btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #E07B00, #C96A00);
  box-shadow: 0 6px 18px rgba(247,147,30,0.45); transform: translateY(-1px);
}
.au-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }

.au-table { width: 100%; border-collapse: collapse; }
.au-table thead tr { border-bottom: 2px solid #f1f5f9; }
.au-table thead th {
  padding: 10px 16px; font-size: 0.72rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em; color: #94a3b8; background: #f8fafc;
}
.au-table tbody tr { border-bottom: 1px solid #f1f5f9; transition: background 0.15s; }
.au-table tbody tr:hover { background: #f8fafc; }
.au-table td { padding: 14px 16px; vertical-align: middle; }

.au-company-badge {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em;
}

.au-btn-action {
  display: inline-flex; align-items: center;
  padding: 6px 16px; border-radius: 20px; border: 1.5px solid #e2e8f0;
  background: #fff; color: #F7931E; font-size: 0.8rem; font-weight: 700;
  cursor: pointer; transition: all 0.15s; text-decoration: none;
}
.au-btn-action:hover {
  background: #FEF1E0; border-color: #F7931E; color: #E07B00; text-decoration: none;
}

.au-banner-info {
  display: flex; align-items: center; text-align: left;
  background-color: #FEF1E0; border-radius: 12px;
  padding: 18px 24px; margin-bottom: 24px;
  border-left: 4px solid #E07B00;
}
.au-banner-icon {
  font-size: 1.8rem; color: #E07B00;
  margin-right: 18px; flex-shrink: 0;
}
.au-banner-title {
  display: block; margin-bottom: 4px;
  color: #1A2238; font-size: 1.05rem; font-weight: 700;
}
.au-banner-text {
  display: block; line-height: 1.5;
  color: #334155; font-size: 0.88rem; font-weight: 500;
}

.dati-empty-notice {
  display: flex;
  align-items: center;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 14px 18px;
  font-size: 0.88rem;
  font-weight: 500;
  color: #166534;
}
.dati-empty-notice .mdi {
  font-size: 1.1rem;
  color: #16a34a;
}

.edit-mode-banner {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #FEF1E0, #FEF1E0);
  border: 1px solid #FBD9AE;
  border-left: 4px solid #F7931E;
  border-radius: 10px;
  padding: 14px 18px;
  font-size: 0.88rem;
  font-weight: 500;
  color: #C96A00;
}
.edit-mode-banner .mdi {
  color: #E07B00;
}
</style>
